import { Button, Dimmer, Loader, Message, Modal } from 'semantic-ui-react';
import { useCallback, useMemo, useState } from 'react';
import { usePreparationOrderDetailTableData } from 'hooks/usePreparationOrderDetailTableData';
import ArticleReplacer from 'components/ArticleReplacer/ArticleReplacer';
import { getMessageFromAxiosError } from 'modules/message-factory/message-factory';
import { updatePackages } from 'services/preparation-orders.service';
import { useAlert } from 'context/AlertProvider';
import { useHistory } from 'react-router-dom';

export default function ArticlesReplacerModal({ preparationOrder }) {
  const alert = useAlert();
  const history = useHistory();
  const [modalIsOpen, setModalIsOpen] = useState(false);
  const articles = usePreparationOrderDetailTableData(preparationOrder);
  const initialReplacements = useMemo(
    () => articles.map((article) => ({ oldSKU: article.sku, newSKU: '' })),
    [articles],
  );
  const [replacements, setReplacements] = useState(initialReplacements);
  const replacementIsComplete = useMemo(
    () => !replacements.some((replacement) => !replacement.newSKU),
    [replacements],
  );
  const [loading, setLoading] = useState(false);
  const [findingReplacement, setFindingReplacement] = useState(false);

  const updateReplacement = useCallback((oldSKU, newSKU, replacements) => {
    const updatedReplacements = replacements.reduce(
      (accumulator, replacement) => {
        return [
          ...accumulator,
          replacement.oldSKU === oldSKU
            ? { oldSKU, ...(newSKU ? { newSKU } : { newSKU: '' }) }
            : replacement,
        ];
      },
      [],
    );
    setReplacements(updatedReplacements);
  }, []);

  const updateArticles = useCallback(
    async (replacements) => {
      try {
        const preparedReplacements = replacements.map((replacement) => ({
          old: replacement.oldSKU,
          new: replacement.newSKU,
        }));
        setLoading(true);
        await updatePackages(preparationOrder.reference, preparedReplacements);
        alert.current.message(
          'Proceso de actualización de sustituciones completo',
          () => {
            setLoading(false);
            history.push('/preparation-orders');
          },
        );
      } catch (error) {
        alert.current.error(getMessageFromAxiosError(error));
        setLoading(false);
      }
    },
    [alert, history, preparationOrder],
  );

  const handleCloseModal = useCallback(
    (event, data) => {
      setReplacements(initialReplacements);
      setModalIsOpen(false);
    },
    [initialReplacements],
  );

  return (
    <Modal
      size="large"
      open={modalIsOpen}
      onClose={handleCloseModal}
      trigger={
        <Button primary onClick={(event, data) => setModalIsOpen(true)}>
          Sustituir artículos
        </Button>
      }
    >
      <Modal.Header>Sustitución de artículos</Modal.Header>
      <Modal.Content scrolling>
        <Dimmer.Dimmable>
          <Dimmer active={loading} inverted>
            <Loader>Sustituyendo artículos…</Loader>
          </Dimmer>
          <Dimmer active={findingReplacement} inverted>
            <Loader>Buscando sustituto…</Loader>
          </Dimmer>
          {!replacementIsComplete && (
            <Message negative role="alert">
              <strong>¡Atención!</strong> Quedan artículos sin sustituir
            </Message>
          )}
          {articles.map((article) => (
            <ArticleReplacer
              key={article.sku}
              article={article}
              setLoading={setFindingReplacement}
              hasReplacement={
                !!replacements.find(
                  (replacement) => replacement.oldSKU === article.sku,
                )?.newSKU
              }
              onAddReplacement={(oldSKU, newSKU) =>
                updateReplacement(oldSKU, newSKU, replacements)
              }
              onRemoveReplacement={(oldSKU) =>
                updateReplacement(oldSKU, null, replacements)
              }
            />
          ))}
        </Dimmer.Dimmable>
      </Modal.Content>
      <Modal.Actions role="contentinfo">
        <Button onClick={handleCloseModal}>Cancelar</Button>
        <Button
          primary
          disabled={!replacementIsComplete}
          onClick={(event, data) => updateArticles(replacements)}
        >
          Sustituir artículos
        </Button>
      </Modal.Actions>
    </Modal>
  );
}
